/**
 * Social Media Post Validation
 * 
 * Comprehensive validation rules for social media posts across different platforms
 */

export interface ValidationError {
  field: string
  message: string
}

export interface ValidationResult {
  isValid: boolean
  errors: ValidationError[]
}

// Platform-specific constraints
const PLATFORM_CONSTRAINTS = {
  INSTAGRAM: {
    captionMaxLength: 2200,
    hashtagMaxCount: 30,
    hashtagMaxLength: 100, // Instagram allows up to 100 chars per hashtag
    mentionMaxCount: 30,
  },
  FACEBOOK: {
    captionMaxLength: 63206,
    hashtagMaxCount: 100, // Facebook doesn't have a strict limit
  },
  TWITTER: {
    captionMaxLength: 280,
    hashtagMaxCount: 10, // Practical limit due to character constraint
  },
  LINKEDIN: {
    captionMaxLength: 3000,
    hashtagMaxCount: 30,
  }
}

type PlatformKey = keyof typeof PLATFORM_CONSTRAINTS

function isPlatformKey(value: string): value is PlatformKey {
  return value in PLATFORM_CONSTRAINTS
}

// Validation functions
export function validateCaption(caption: string, platform?: string): ValidationError | null {
  // Check if caption is empty or just whitespace
  if (!caption || caption.trim().length === 0) {
    return { field: 'caption', message: 'Caption is required' }
  }

  // Check minimum length
  if (caption.trim().length < 10) {
    return { field: 'caption', message: 'Caption must be at least 10 characters long' }
  }

  // Check platform-specific maximum length
  if (platform && isPlatformKey(platform)) {
    const maxLength = PLATFORM_CONSTRAINTS[platform].captionMaxLength
    if (caption.length > maxLength) {
      return { 
        field: 'caption', 
        message: `Caption exceeds ${platform} limit of ${maxLength.toLocaleString()} characters` 
      }
    }
  }

  return null
}

export function validateHashtags(hashtags: string[], platform?: string): ValidationError[] {
  const errors: ValidationError[] = []

  // Check for empty hashtags
  const emptyHashtags = hashtags.filter(tag => tag === '#' || tag.trim() === '')
  if (emptyHashtags.length > 0) {
    errors.push({ 
      field: 'hashtags', 
      message: 'Remove empty hashtags' 
    })
  }

  // Check for invalid hashtag format
  const invalidHashtags = hashtags.filter(tag => {
    // Hashtag should start with # and contain at least one character after
    const hashtagRegex = /^#[\w]+$/
    return !hashtagRegex.test(tag)
  })
  
  if (invalidHashtags.length > 0) {
    errors.push({ 
      field: 'hashtags', 
      message: `Invalid hashtags: ${invalidHashtags.join(', ')}. Hashtags can only contain letters, numbers, and underscores.` 
    })
  }

  // Check hashtag length
  const longHashtags = hashtags.filter(tag => tag.length > 100)
  if (longHashtags.length > 0) {
    errors.push({ 
      field: 'hashtags', 
      message: 'Hashtags cannot exceed 100 characters' 
    })
  }

  // Check platform-specific hashtag count
  if (platform && isPlatformKey(platform)) {
    const maxCount = PLATFORM_CONSTRAINTS[platform].hashtagMaxCount
    if (hashtags.length > maxCount) {
      errors.push({ 
        field: 'hashtags', 
        message: `${platform} allows a maximum of ${maxCount} hashtags` 
      })
    }
  }

  // Check for duplicate hashtags (case-insensitive)
  const uniqueHashtags = new Set(hashtags.map(tag => tag.toLowerCase()))
  if (uniqueHashtags.size < hashtags.length) {
    errors.push({ 
      field: 'hashtags', 
      message: 'Duplicate hashtags detected' 
    })
  }

  return errors
}

export function validateScheduledTime(scheduledTime: string | null): ValidationError | null {
  if (!scheduledTime) {
    return null // Scheduled time is optional
  }

  const scheduledDate = new Date(scheduledTime)
  const now = new Date()

  // Check if date is valid
  if (isNaN(scheduledDate.getTime())) {
    return { 
      field: 'scheduled_time', 
      message: 'Invalid date format' 
    }
  }

  // Check if date is in the past
  if (scheduledDate <= now) {
    return { 
      field: 'scheduled_time', 
      message: 'Scheduled time must be in the future' 
    }
  }

  // Check if date is too far in the future (1 year)
  const oneYearFromNow = new Date()
  oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1)
  if (scheduledDate > oneYearFromNow) {
    return { 
      field: 'scheduled_time', 
      message: 'Cannot schedule posts more than 1 year in advance' 
    }
  }

  return null
}

export function validateProfileSelection(profileId: string | null): ValidationError | null {
  if (!profileId || profileId.trim() === '') {
    return { 
      field: 'profile_id', 
      message: 'Please select a social media profile' 
    }
  }
  return null
}

export function validateImageUrls(imageUrls: string[], platform?: string): ValidationError[] {
  const errors: ValidationError[] = []

  // Instagram specific validations
  if (platform === 'INSTAGRAM') {
    if (imageUrls.length > 10) {
      errors.push({ 
        field: 'image_urls', 
        message: 'Instagram allows a maximum of 10 images per post' 
      })
    }
  }

  // Validate image URLs
  const invalidUrls = imageUrls.filter(url => {
    try {
      new URL(url)
      return false
    } catch {
      return true
    }
  })

  if (invalidUrls.length > 0) {
    errors.push({ 
      field: 'image_urls', 
      message: 'Some image URLs are invalid' 
    })
  }

  return errors
}

// Main validation function
export function validateSocialPost(
  formData: {
    caption: string
    profile_id: string
    scheduled_time: string | null
    hashtags: string[]
    image_urls: string[]
  },
  platform?: string
): ValidationResult {
  const errors: ValidationError[] = []

  // Validate caption
  const captionError = validateCaption(formData.caption, platform)
  if (captionError) errors.push(captionError)

  // Validate profile selection
  const profileError = validateProfileSelection(formData.profile_id)
  if (profileError) errors.push(profileError)

  // Validate scheduled time
  const scheduledTimeError = validateScheduledTime(formData.scheduled_time)
  if (scheduledTimeError) errors.push(scheduledTimeError)

  // Validate hashtags
  const hashtagErrors = validateHashtags(formData.hashtags, platform)
  errors.push(...hashtagErrors)

  // Validate images
  const imageErrors = validateImageUrls(formData.image_urls, platform)
  errors.push(...imageErrors)

  return {
    isValid: errors.length === 0,
    errors
  }
}

// Real-time validation helpers
export function getFieldError(errors: ValidationError[], fieldName: string): string | null {
  const error = errors.find(e => e.field === fieldName)
  return error ? error.message : null
}

export function hasFieldError(errors: ValidationError[], fieldName: string): boolean {
  return errors.some(e => e.field === fieldName)
}
